import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Subject, catchError, finalize, map, switchMap, take, tap } from 'rxjs';

import { SortDirectionType } from '../../../core/models/sort-direction';
import { Dropdown } from '../../../shared/components/dropdown/dropdown';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { formatLocalDate, formatLocalTime } from '../../../shared/utils/date-time/date-time.utils';
import { triggerBlobDownload } from '../../../shared/utils/file-download/file-download.utils';
import { PageItem, getPageItems } from '../../../shared/utils/pagination/pagination.utils';
import { EMPTY_CELL } from '../../../shared/utils/table/table.utils';
import { AuditEventSummary } from '../../models/activity-log';
import { AdminActionType } from '../../models/admin-action-type';
import { AdminUserFilter } from '../../models/admin-user-filter';
import { AuditActorType } from '../../models/audit-actor-type';
import { AuditEventCategory } from '../../models/audit-event-category';
import { EventType } from '../../models/event-type';
import { PatientFilter } from '../../../shared/models/patient-filter';
import { UserPatientRelationshipType } from '../../models/user-patient-relationship-type';
import { ActivityLogsService, AuditEventSearchRequest } from '../../services/activity-logs.service';
import { AuditLogFilterStateService } from '../../services/audit-log-filter-state.service';
import {
  ADMIN_ACTION_DETAIL_ROUTE,
  resolveDisclosureDetailRoute,
} from '../../utils/row-detail/row-detail.utils';
import { toTypeBadgeClass } from '../../utils/type-badge/type-badge.utils';
import { PatientSearchModal } from '../../../shared/components/patient-search-modal/patient-search-modal';
import { UserSearchModal } from '../user-search-modal/user-search-modal';

/** Action-type dropdown value prefixes, routing the selection to the matching request filter. */
const DISCLOSURE_ACTION_PREFIX = 'event:';
const ADMIN_ACTION_PREFIX = 'admin:';

/** "Performed By" role lines (see buildActorRole for the proxy case, which is composed). */
const STAFF_ROLE_LABEL = 'Staff';
const PATIENT_ROLE_LABEL = 'Patient';
/** Used for a portal user whose relationship to the subject patient isn't resolvable. */
const FALLBACK_PORTAL_ROLE_LABEL = 'Portal User';

/** Which filter modal is currently open. */
type ActiveModal = 'patient' | 'user' | null;

interface DateRangeOption {
  /** Dropdown value: a calendar keyword (`today`/`yesterday`) or a rolling day count. */
  value: string;
  label: string;
}

interface FilterOption {
  /** Dropdown value: a prefixed id (`event:1`/`admin:2`), or the sentinel `'all'`. */
  value: string;
  label: string;
}

/** A single audit-log row, projected for the table. */
interface AuditLogRow {
  id: number;
  /**
   * Stable `@for` key. `id` alone is not unique across the feed — it is the row's primary key
   * *within its own ledger* — so the two categories can collide on the same page. Pairing the
   * category with the id is what makes the key unique.
   */
  trackKey: string;
  /** True for disclosure rows; false for admin-action rows (they drill down to different screens). */
  isDisclosure: boolean;
  /**
   * The detail screen this row opens, or null when it opens nothing. Disclosures resolve it from
   * their feature (see {@link resolveDisclosureDetailRoute}) — appointments have their own screen,
   * medications have none — while admin actions always open theirs, since that ledger has no
   * `feature` for the resolver to read.
   *
   * This one field drives both the drill-down and every affordance that advertises it (tab stop,
   * pointer cursor, link-styled patient name), so a non-navigable row can never look clickable.
   * Null only for medication *disclosures*, which are rendered fully inert.
   */
  detailRoute: string | null;
  date: string;
  time: string;
  patientName: string;
  medicalRecordNumber: string;
  eventLabel: string;
  /** Secondary line under the event, served ready-to-render by the API. */
  eventDetail: string | null;
  performedByName: string;
  performedByRole: string | null;
  typeCategory: string;
  /** Chip-color class suffix keyed off the type category (see {@link toTypeBadgeClass}). */
  typeClass: string;
  isFlagged: boolean;
  /**
   * Announcement for the row. An `aria-label` on a `role="row"` *replaces* its accessible name — so
   * the label has to carry the columns a sighted user reads at a glance, not just the event, or
   * tabbing the grid announces far less than the row shows. Navigable rows lead with "View details
   * for"; an inert row omits it rather than offering an action it doesn't have.
   */
  ariaLabel: string;
}

@Component({
  selector: 'app-activity-logs',
  standalone: true,
  imports: [
    Dropdown,
    FormsModule,
    IconComponent,
    ProgressSpinnerComponent,
    PatientSearchModal,
    UserSearchModal,
  ],
  templateUrl: './activity-logs.html',
  styleUrls: ['./activity-logs.scss'],
})
export class ActivityLogs implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly activityLogsService = inject(ActivityLogsService);
  private readonly filterState = inject(AuditLogFilterStateService);
  private readonly loadTrigger$ = new Subject<void>();

  readonly pageSizeOptions: number[] = [10, 25, 50];
  readonly dateRangeOptions: DateRangeOption[] = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' },
    { value: '365', label: 'Last 12 Months' },
  ];

  /** The "All Actions" sentinel option shown above the action types. */
  readonly allActionsOption: FilterOption = { value: 'all', label: 'All Actions' };

  // One flat list (per the wireframe) rather than branch-grouped options, using the labels
  // staff recognise rather than the raw enum names ("Export" for Download, "Approve" for
  // Confirm). The unified feed still spans two branches, so the `event:` / `admin:` prefix
  // routes the selection to `eventTypeId` or `adminActionTypeId` in buildRequest.
  readonly actionTypeOptions: FilterOption[] = [
    { value: `${DISCLOSURE_ACTION_PREFIX}${EventType.View}`, label: 'View' },
    { value: `${DISCLOSURE_ACTION_PREFIX}${EventType.Print}`, label: 'Print' },
    { value: `${DISCLOSURE_ACTION_PREFIX}${EventType.Download}`, label: 'Export' },
    { value: `${ADMIN_ACTION_PREFIX}${AdminActionType.AddNote}`, label: 'Add Note' },
    { value: `${ADMIN_ACTION_PREFIX}${AdminActionType.Confirm}`, label: 'Approve' },
    { value: `${ADMIN_ACTION_PREFIX}${AdminActionType.Deny}`, label: 'Deny' },
    // The three access-control actions, written by the Portal Access Control card on the Access &
    // Controls detail screen: a block and a restore are HIPAA-audited events, and
    // a staff member asked to account for one has to be able to find it here.
    { value: `${ADMIN_ACTION_PREFIX}${AdminActionType.Block}`, label: 'Block Access' },
    { value: `${ADMIN_ACTION_PREFIX}${AdminActionType.Restore}`, label: 'Restore Access' },
    { value: `${ADMIN_ACTION_PREFIX}${AdminActionType.Revoke}`, label: 'Revoke Proxy Access' },
    // Create, Update, Deactivate and Assign stay out: this list only carries action types that
    // have a producer, and nothing in the app writes those yet.
  ];

  /**
   * What the Action Type dropdown renders: the "All Actions" sentinel followed by
   * the action types. Composed here rather than in the template so the sentinel
   * can't drift out of the list the control actually offers, and so
   * `actionTypeOptions` stays the pure list the request-building code reasons about.
   */
  readonly actionTypeFilterOptions: FilterOption[] = [
    this.allActionsOption,
    ...this.actionTypeOptions,
  ];

  readonly isLoading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly entries = signal<AuditEventSummary[]>([]);
  readonly totalRecords = signal(0);

  readonly isExporting = signal(false);
  readonly exportError = signal<string | null>(null);

  /**
   * Polite live-region text narrating the export from start to finish. The download itself is
   * invisible to a screen reader — the file just appears in the browser's download shelf — so
   * without this a non-sighted user gets no confirmation that the button did anything.
   *
   * It announces the **start** as well as the end: `aria-busy` on a button is not an announced state
   * change in mainstream AT, so a 50,000-row export would otherwise be many seconds of silence after
   * the click. Each attempt overwrites the previous text, so a repeat export re-announces rather
   * than sitting on stale text.
   */
  readonly exportStatus = signal('');

  /**
   * The request the **currently displayed** results came from, captured when the search resolves.
   *
   * The export sends this rather than a freshly built request: an audit artifact that silently
   * disagrees with the grid the user was looking at is worse than a stale one, and rebuilding from
   * live filter state would export whatever is selected *now* — which, mid-search, isn't what is on
   * screen.
   */
  private readonly appliedRequest = signal<AuditEventSearchRequest | null>(null);

  /**
   * Drives the export. A `switchMap` over this is what makes a filter change cancel an in-flight
   * export: `null` means "cancel", so the user can never end up saving a file that describes
   * filters the grid has already moved on from.
   */
  private readonly exportTrigger$ = new Subject<AuditEventSearchRequest | null>();

  // Every filter is applied server-side; a change reissues the search from page 1
  // so pagination stays in sync with the filters. These live in a root-scoped state
  // service so the view survives navigating into a disclosure detail and back; they
  // reset when the user leaves the audit-log area (see AuditLogFilterStateService).
  readonly selectedDateRange = this.filterState.selectedDateRange;
  readonly selectedActionType = this.filterState.selectedActionType;
  readonly flaggedOnly = this.filterState.flaggedOnly;
  readonly patientFilter = this.filterState.patientFilter;
  readonly adminUserFilter = this.filterState.adminUserFilter;

  readonly currentPage = this.filterState.currentPage;
  readonly pageSize = this.filterState.pageSize;

  readonly activeModal = signal<ActiveModal>(null);

  /** The export control, so dismissing the error banner can hand focus back to it. */
  private readonly exportButton = viewChild<ElementRef<HTMLButtonElement>>('exportButton');

  // Rows come pre-filtered and pre-paged from the server, so this only projects
  // the loaded entries for display.
  readonly rows = computed<AuditLogRow[]>(() => this.entries().map((entry) => this.toRow(entry)));

  /** One-line summary of the applied patient filter, shown in the PATIENT pill. */
  readonly patientFilterSummary = computed<string | null>(() => {
    const filter = this.patientFilter();
    if (!filter) {
      return null;
    }

    const name = [filter.firstName, filter.lastName].filter(Boolean).join(' ').trim();
    if (name) {
      return name;
    }
    if (filter.medicalRecordNumber) {
      return `MRN ${filter.medicalRecordNumber}`;
    }
    if (filter.dateOfBirth) {
      return `DOB ${filter.dateOfBirth}`;
    }
    return null;
  });

  /** One-line summary of the applied user/employee filter, shown in the USER pill. */
  readonly userFilterSummary = computed<string | null>(() => {
    const filter = this.adminUserFilter();
    if (!filter) {
      return null;
    }
    const name = [filter.firstName, filter.lastName].filter(Boolean).join(' ').trim();
    return name || null;
  });

  readonly hasActiveFilters = computed(
    () =>
      this.patientFilter() !== null ||
      this.adminUserFilter() !== null ||
      this.selectedActionType() !== 'all' ||
      this.flaggedOnly(),
  );

  readonly totalPages = computed(() => {
    const total = this.totalRecords();
    return total === 0 ? 1 : Math.ceil(total / this.pageSize());
  });

  readonly pageTokens = computed<PageItem[]>(() =>
    getPageItems(this.currentPage(), this.totalPages()),
  );

  /**
   * The export button's label. It names the number of rows the current filters match so the control
   * can't be read as exporting the whole ledger; until the first search resolves there is no count
   * to name, so it falls back to the bare verb.
   *
   * Deliberately keyed off `appliedRequest` rather than `isLoading`: paging and every filter change
   * flip `isLoading`, so reading it here would make the count — the whole point of the label —
   * flicker away and back on every interaction. `canExport()` handles disabling during a load.
   */
  readonly exportLabel = computed(() => {
    const total = this.totalRecords();
    if (this.appliedRequest() === null || total === 0) {
      return 'Export CSV';
    }
    return `Export ${total.toLocaleString()} result${total === 1 ? '' : 's'}`;
  });

  /**
   * Disabled when there is nothing to export (no resolved search, an error, or zero matches) and
   * while an export is in flight — a 50,000-row export is slow enough to invite a double-click.
   */
  readonly canExport = computed(
    () =>
      !this.isExporting() &&
      !this.isLoading() &&
      this.loadError() === null &&
      this.totalRecords() > 0 &&
      this.appliedRequest() !== null,
  );

  readonly showingSummary = computed(() => {
    const total = this.totalRecords();
    if (total === 0) {
      return 'Showing 0 of 0 events';
    }

    const start = (this.currentPage() - 1) * this.pageSize() + 1;
    const end = Math.min(start + this.entries().length - 1, total);
    return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()} events`;
  });

  ngOnInit(): void {
    this.loadTrigger$
      .pipe(
        tap(() => {
          this.isLoading.set(true);
          this.loadError.set(null);
          this.exportError.set(null);
        }),
        switchMap(() => {
          // Built once and carried through, so the request that produced these results is the exact
          // object the export can later re-send (see appliedRequest).
          const request = this.buildRequest();
          return this.activityLogsService.searchAuditEvents(request).pipe(
            map((result) => ({ result, request })),
            catchError(() => {
              this.loadError.set('Unable to load the audit log right now.');
              this.entries.set([]);
              this.totalRecords.set(0);
              this.appliedRequest.set(null);
              this.isLoading.set(false);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ result, request }) => {
          this.entries.set(result.entries);
          this.totalRecords.set(result.totalRecordCount);
          this.appliedRequest.set(request);
          this.loadError.set(null);
          this.isLoading.set(false);
        },
      });

    this.exportTrigger$
      .pipe(
        switchMap((request) => {
          // `null` is the cancel signal. The outgoing request's finalize has already cleared
          // `isExporting`, so all that's left is telling the user why no file arrived — the
          // alternative is a click on "Export 1,247 results" that produces silence forever.
          // `reload()` only sends `null` while an export is actually in flight, so this can't
          // announce a cancellation that never happened.
          if (!request) {
            this.exportStatus.set('Export cancelled because the filters changed.');
            return EMPTY;
          }

          return this.activityLogsService.exportAuditEvents(request).pipe(
            // One click, one file — explicitly, rather than trusting the source to complete.
            take(1),
            tap(({ blob, fileName }) => {
              triggerBlobDownload(blob, fileName);
              this.exportStatus.set(`Export complete. ${fileName} has been downloaded.`);
            }),
            catchError((error: unknown) =>
              this.activityLogsService
                .readExportErrorMessage(error)
                .pipe(tap((message) => this.exportError.set(message))),
            ),
            // The only place `isExporting` is cleared, so success, failure and cancellation all
            // release the button — including the unsubscribe that a filter change triggers, which
            // is what stops a stalled export from latching the control off for good.
            finalize(() => this.isExporting.set(false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Load with whatever state the filter-state service holds — on first entry that's
    // the defaults; when returning from a disclosure detail it's the preserved filters
    // and page, so we deliberately do not reset to page 1 here.
    this.loadTrigger$.next();
  }

  onDateRangeChange(value: string): void {
    this.selectedDateRange.set(value);
    this.reload();
  }

  onActionTypeChange(value: string): void {
    this.selectedActionType.set(value);
    // A disclosure/admin action type conflicts with the opposing branch's filters;
    // clear them so the request the API sees is never contradictory.
    if (value.startsWith(DISCLOSURE_ACTION_PREFIX)) {
      this.clearAdminSideFilters();
    } else if (value.startsWith(ADMIN_ACTION_PREFIX)) {
      this.clearDisclosureSideFilters();
    }
    this.reload();
  }

  onFlaggedOnlyChange(value: boolean): void {
    // "Flagged only" narrows BOTH ledgers rather than selecting one: disclosures above a plain
    // View, and admin decisions. The API treats it that way too, so it deliberately clears
    // nothing — "flagged denials" is a valid search.
    //
    // Verified against the API (MedOne.Conduit.Api.PatientPortalAdmin, main):
    // `AuditEventSearchUtility.HasDisclosureOnlyFilter` covers only patient criteria and
    // `EventTypeId` — its doc comment states "FlaggedOnly does NOT count" — and
    // `GetAdminActionLogCriteria` applies `request.FlaggedOnly` to the admin branch via
    // `AdminActionLogDtoMappingUtility.FlaggedActionTypeIds`. `AuditEventSearchRequestValidator`
    // likewise omits it from the contradiction rule, so this combination is never rejected.
    this.flaggedOnly.set(value);
    this.reload();
  }

  onPageSizeChange(value: string): void {
    this.pageSize.set(Number(value));
    this.reload();
  }

  goToPage(page: number): void {
    const nextPage = Math.max(1, Math.min(page, this.totalPages()));
    if (nextPage === this.currentPage()) {
      return;
    }

    this.currentPage.set(nextPage);
    this.loadTrigger$.next();
  }

  openPatientModal(): void {
    this.activeModal.set('patient');
  }

  openUserModal(): void {
    this.activeModal.set('user');
  }

  closeModal(): void {
    this.activeModal.set(null);
  }

  applyPatientFilter(filter: PatientFilter): void {
    // Patient (disclosure branch) and user (admin branch) filters are mutually exclusive.
    this.clearAdminSideFilters();
    this.patientFilter.set(filter);
    this.closeModal();
    this.reload();
  }

  clearPatientFilter(): void {
    this.closeModal();
    if (this.patientFilter() === null) {
      return;
    }

    this.patientFilter.set(null);
    this.reload();
  }

  applyUserFilter(filter: AdminUserFilter): void {
    // User (admin branch) and patient/flagged (disclosure branch) filters are mutually exclusive.
    this.clearDisclosureSideFilters();
    this.adminUserFilter.set(filter);
    this.closeModal();
    this.reload();
  }

  clearUserFilter(): void {
    this.closeModal();
    if (this.adminUserFilter() === null) {
      return;
    }

    this.adminUserFilter.set(null);
    this.reload();
  }

  /**
   * Download the current result set as a CSV.
   *
   * Re-sends the request the displayed results came from, so the file matches the grid: filters and
   * sort carry over, and the server ignores the paging fields to return every matching row.
   *
   * The response is deliberately not treated as a file until it is known to be one: the endpoint
   * answers a validation failure or an over-ceiling match with a 400 JSON `ResponsePackage`, and the
   * service also rejects a 2xx that isn't `text/csv` — saving either as `audit-log-export-….csv`
   * would hand the user an error dressed as an audit report.
   *
   * The `canExport()` guard is authoritative, not a belt-and-braces double-check: **while an export
   * is in flight** the button is marked `aria-disabled` rather than `disabled`, so it keeps keyboard
   * focus (a `disabled` control gets blurred by the browser, which drops the user out of the toolbar
   * and silences the `aria-busy` announcement). An aria-disabled button is still clickable, so this
   * guard is the only thing actually stopping a double-submit. When the button is inert for any other
   * reason — no resolved search, a load error, zero matches — it is genuinely `disabled`, because
   * there is no focus to preserve and a silently-ignored Enter press is worse than the browser's
   * own "this control is unavailable" feedback.
   */
  exportCsv(): void {
    const request = this.appliedRequest();
    if (!this.canExport() || !request) {
      return;
    }

    this.isExporting.set(true);
    this.exportError.set(null);
    // Announced rather than cleared: the live region carries both ends of the export, so the click
    // itself is confirmed instead of leaving a screen-reader user waiting on nothing.
    this.exportStatus.set('Preparing export…');
    this.exportTrigger$.next(request);
  }

  /**
   * Dismiss the export error banner, moving focus back to the export button.
   *
   * Dismissing destroys the element the user was focused on, which would otherwise drop focus to
   * `<body>` and lose a keyboard user's place entirely. The export button is where they came from
   * and what they most likely want next.
   */
  clearExportError(): void {
    this.exportError.set(null);
    this.exportButton()?.nativeElement.focus();
  }

  /**
   * Open the drill-down for the clicked row. The two categories live in separate ledgers, so a
   * row's id is only meaningful against its own detail screen — and within the disclosure ledger an
   * appointment opens a different screen from everything else, which is why the destination is
   * resolved at map time rather than branched on here.
   *
   * Rows without a detail screen (medication reads) return early. The template still binds the
   * handler — Angular can't conditionally attach a listener without duplicating the row — so this
   * guard, not the missing affordance, is what actually makes those rows inert.
   */
  onRowClick(row: AuditLogRow): void {
    if (!row.detailRoute) {
      return;
    }

    this.router.navigate([row.detailRoute, row.id]);
  }

  /**
   * Space activates a focused row like Enter. It is handled separately only because it also has to
   * suppress the browser's page-scroll — and only for a row that actually opens something, so an
   * inert row keeps Space doing what the rest of the grid does.
   *
   * Typed `Event`, not `KeyboardEvent`: Angular types `$event` as `Event` for `keydown.*` pseudo-
   * events, so narrowing this fails the template type-check.
   */
  onRowSpace(event: Event, row: AuditLogRow): void {
    if (!row.detailRoute) {
      return;
    }

    event.preventDefault();
    this.onRowClick(row);
  }

  private toRow(entry: AuditEventSummary): AuditLogRow {
    const patientName = entry.patientDisplayName ?? EMPTY_CELL;
    const performedByName = entry.actorDisplayName || 'System';
    const isDisclosure = entry.auditEventCategoryId === AuditEventCategory.PatientDataDisclosure;
    // Disclosures only: both the per-feature destination and the missing-detail defect are
    // properties of the disclosure ledger. Admin actions open a single screen off a contract that
    // has no `feature` at all, so running them through the resolver could only send them somewhere
    // wrong or make a working drill-down inert.
    const detailRoute = isDisclosure
      ? resolveDisclosureDetailRoute(entry.feature)
      : ADMIN_ACTION_DETAIL_ROUTE;
    return {
      id: entry.sourceId,
      trackKey: `${entry.auditEventCategoryId}:${entry.sourceId}`,
      isDisclosure,
      detailRoute,
      date: formatLocalDate(entry.createdOnUtc),
      time: formatLocalTime(entry.createdOnUtc),
      patientName,
      medicalRecordNumber: entry.medicalRecordNumber ?? EMPTY_CELL,
      eventLabel: entry.actionLabel,
      eventDetail: entry.eventSubtitle,
      performedByName,
      performedByRole: this.buildActorRole(entry),
      typeCategory: entry.typeCategory,
      typeClass: toTypeBadgeClass(entry.typeCategory),
      isFlagged: entry.isFlagged,
      ariaLabel: this.buildRowAriaLabel(entry, patientName, performedByName, !!detailRoute),
    };
  }

  /**
   * Compose the row's announcement. Admin actions have no subject patient, so the patient clause is
   * dropped rather than announced as the em-dash placeholder the cell shows. A row with no detail
   * screen reads as a plain description — promising "View details for" on a row that can't be
   * activated is worse than saying less.
   */
  private buildRowAriaLabel(
    entry: AuditEventSummary,
    patientName: string,
    performedByName: string,
    isNavigable: boolean,
  ): string {
    const parts = [
      isNavigable ? `View details for ${entry.actionLabel}` : entry.actionLabel,
      entry.patientDisplayName ? `patient ${patientName}` : null,
      `by ${performedByName}`,
      `on ${formatLocalDate(entry.createdOnUtc)} at ${formatLocalTime(entry.createdOnUtc)}`,
      entry.isFlagged ? 'flagged' : null,
    ];
    return parts.filter(Boolean).join(', ');
  }

  /**
   * Derive the "Performed By" role line: admin actions are "Staff"; a disclosure is "Patient" when
   * the acting portal user is the subject patient themselves, and "Proxy ({relationship})" when
   * they accessed someone else's record. Self-access reading as "Patient" matches the disclosure
   * detail subtitle.
   */
  private buildActorRole(entry: AuditEventSummary): string | null {
    if (entry.actorTypeId === AuditActorType.AdminUser) {
      return STAFF_ROLE_LABEL;
    }
    if (entry.actorTypeId !== AuditActorType.PortalUser) {
      return null;
    }
    if (entry.actorPatientRelationshipTypeId === UserPatientRelationshipType.Self) {
      return PATIENT_ROLE_LABEL;
    }
    // The relationship only comes back for a currently-active association, so a proxy whose access
    // has since been revoked (or a log with no resolvable association) has none — fall back to the
    // unqualified role rather than claiming a relationship we can't name.
    return entry.actorPatientRelationshipType
      ? `Proxy (${entry.actorPatientRelationshipType})`
      : FALLBACK_PORTAL_ROLE_LABEL;
  }

  /** Clear the admin-branch filters (user filter + any admin action-type selection). */
  private clearAdminSideFilters(): void {
    this.adminUserFilter.set(null);
    if (this.selectedActionType().startsWith(ADMIN_ACTION_PREFIX)) {
      this.selectedActionType.set('all');
    }
  }

  /**
   * Clear the disclosure-branch filters (patient filter, disclosure action type). "Flagged only" is
   * deliberately left alone — it applies to both ledgers, so it stays applied across a branch
   * switch (see {@link onFlaggedOnlyChange}).
   */
  private clearDisclosureSideFilters(): void {
    this.patientFilter.set(null);
    if (this.selectedActionType().startsWith(DISCLOSURE_ACTION_PREFIX)) {
      this.selectedActionType.set('all');
    }
  }

  /**
   * Reset to the first page and reload from scratch (initial load / filter change).
   *
   * Cancels any in-flight export: the file it would save describes the filters that were on screen
   * when it started, and those have just changed. An audit artifact that quietly disagrees with the
   * grid the user was looking at is the outcome worth spending a round-trip to avoid. Paging does
   * not cancel — the export ignores paging, so a page change can't make its contents wrong.
   */
  private reload(): void {
    // Guarded, not unconditional: the cancel branch announces the cancellation in the live region,
    // so sending `null` when nothing is running would tell the user an export was cancelled when
    // they never started one.
    if (this.isExporting()) {
      this.exportTrigger$.next(null);
    }
    this.currentPage.set(1);
    this.loadTrigger$.next();
  }

  private buildRequest(): AuditEventSearchRequest {
    const { createdAfterUtc, createdBeforeUtc } = this.getDateWindow();
    const action = this.selectedActionType();
    return {
      // PatientFilter / AdminUserFilter are structurally the API's patient/adminUser criteria.
      patient: this.patientFilter(),
      adminUser: this.adminUserFilter(),
      createdAfterUtc,
      createdBeforeUtc,
      eventTypeId: this.toActionId(action, DISCLOSURE_ACTION_PREFIX),
      flaggedOnly: this.flaggedOnly(),
      adminActionTypeId: this.toActionId(action, ADMIN_ACTION_PREFIX),
      pageNumber: this.currentPage(),
      pageSize: this.pageSize(),
      sortColumn: 'CreatedOnUtc',
      sortDirection: SortDirectionType.Descending,
    };
  }

  /** Extract the numeric id from a prefixed action-type value, or null when it's another branch. */
  private toActionId(value: string, prefix: string): number | null {
    return value.startsWith(prefix) ? Number(value.slice(prefix.length)) : null;
  }

  /**
   * Resolve the selected date-range option into a search window. `today`/`yesterday`
   * are calendar-aligned to local midnight (yesterday is bounded on both ends); the
   * numeric options are a rolling window of that many days back from now.
   */
  private getDateWindow(): { createdAfterUtc: string; createdBeforeUtc: string | null } {
    const value = this.selectedDateRange();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    if (value === 'today') {
      return { createdAfterUtc: startOfToday.toISOString(), createdBeforeUtc: null };
    }

    if (value === 'yesterday') {
      const startOfYesterday = new Date(startOfToday);
      startOfYesterday.setDate(startOfYesterday.getDate() - 1);
      return {
        createdAfterUtc: startOfYesterday.toISOString(),
        createdBeforeUtc: startOfToday.toISOString(),
      };
    }

    const createdAfter = new Date();
    createdAfter.setDate(createdAfter.getDate() - Number(value));
    return { createdAfterUtc: createdAfter.toISOString(), createdBeforeUtc: null };
  }
}
